import { apiService, buildODataQuery, odataString } from './api.service'
import {
  Asset,
  AssetFileType,
  AssetFolder,
  AssetUploadRequest,
  AssetSearchFilters,
  PaginatedResponse,
  PaginationRequest
} from '../types'

/** Asset kind as stored by the API (mirrors the `AssetType` enum). */
export type AssetKind =
  | 'Image' | 'Video' | 'Audio' | 'Document' | 'Spreadsheet' | 'Presentation' | 'Archive' | 'Other'

/** Shape of an asset as returned by `GET /v1/assets` (AssetODataDto). */
export interface AssetODataDto {
  id: string
  tenantId?: string
  name: string
  type: AssetKind | string
  size: number
  url: string
  thumbnailUrl?: string | null
  folderId?: string | null
  version: number
  tags?: string[] | null
  createdAt: string
  updatedAt: string
}

const EXTENSION_MIME: Record<string, AssetFileType> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  webm: 'video/webm',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  js: 'application/javascript',
}

const KIND_FALLBACK_MIME: Record<string, AssetFileType> = {
  Image: 'image/png',
  Video: 'video/mp4',
  Document: 'application/pdf',
  Spreadsheet: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  Presentation: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
}

/** Best-effort MIME type from the file name, falling back to the asset kind. */
export const toFileType = (name: string, url: string, kind: string): AssetFileType => {
  const source = name.includes('.') ? name : url
  const ext = source.split('?')[0].split('.').pop()?.toLowerCase() ?? ''
  return EXTENSION_MIME[ext] ?? KIND_FALLBACK_MIME[kind] ?? 'text/plain'
}

/** Map a MIME type back to the API's asset kind for server-side filtering. */
export const toAssetKind = (fileType: string): AssetKind => {
  if (fileType.startsWith('image/')) return 'Image'
  if (fileType.startsWith('video/')) return 'Video'
  if (fileType.startsWith('audio/')) return 'Audio'
  if (fileType.includes('spreadsheet')) return 'Spreadsheet'
  if (fileType.includes('presentation')) return 'Presentation'
  if (fileType === 'application/pdf' || fileType.includes('wordprocessing') || fileType.startsWith('text/')) {
    return 'Document'
  }
  return 'Other'
}

/** Convert the API DTO into the `Asset` model used by the add-in components. */
export const mapAsset = (dto: AssetODataDto): Asset => ({
  id: dto.id,
  name: dto.name,
  fileType: toFileType(dto.name, dto.url, dto.type),
  fileSize: dto.size,
  // The API returns paths like `/api/files/{tenant}/{file}`; make them absolute.
  url: apiService.resolveUrl(dto.url),
  thumbnailUrl: dto.thumbnailUrl ? apiService.resolveUrl(dto.thumbnailUrl) : undefined,
  metadata: { version: String(dto.version), kind: dto.type },
  tags: dto.tags ?? [],
  folderId: dto.folderId ?? undefined,
  createdAt: dto.createdAt,
  updatedAt: dto.updatedAt,
  createdBy: '',
  permissions: [],
})

const buildAssetFilter = (filters?: AssetSearchFilters): string | undefined => {
  if (!filters) return undefined
  const clauses: string[] = []
  if (filters.folderId) clauses.push(`folderId eq ${filters.folderId}`)
  if (filters.fileTypes?.length) {
    const kinds = Array.from(new Set(filters.fileTypes.map(toAssetKind)))
    const kindClause = kinds.map(k => `type eq ${odataString(k)}`).join(' or ')
    clauses.push(kinds.length > 1 ? `(${kindClause})` : kindClause)
  }
  return clauses.length ? clauses.join(' and ') : undefined
}

export class AssetService {
  private readonly basePath = '/v1/assets'

  private async list(
    filters?: AssetSearchFilters,
    pagination?: PaginationRequest,
    search?: string
  ): Promise<PaginatedResponse<Asset>> {
    const params = buildODataQuery(
      pagination,
      buildAssetFilter(filters),
      search ? { $search: search } : undefined
    )
    const page = await apiService.getPaginated<AssetODataDto>(this.basePath, params, pagination)
    let items = page.items.map(mapAsset)
    // The API filters by coarse kind; narrow to the exact MIME types requested.
    if (filters?.fileTypes?.length) {
      const wanted = new Set<string>(filters.fileTypes)
      items = items.filter(a => wanted.has(a.fileType))
    }
    return { ...page, items }
  }

  async getAssets(
    params?: PaginationRequest & AssetSearchFilters
  ): Promise<PaginatedResponse<Asset>> {
    const { fileTypes, tags, folderId, dateRange, sizeRange, brandCompliant, ...pagination } = params ?? {}
    return this.list({ fileTypes, tags, folderId, dateRange, sizeRange, brandCompliant }, pagination)
  }

  async getAsset(id: string): Promise<Asset> {
    const response = await apiService.get<AssetODataDto>(`${this.basePath}/${id}`)
    return mapAsset(response.data)
  }

  async uploadAsset(
    request: AssetUploadRequest,
    onProgress?: (progress: number) => void
  ): Promise<Asset> {
    const response = await apiService.upload<AssetODataDto>(
      `${this.basePath}/upload`,
      request.file,
      onProgress
    )

    return mapAsset(response.data)
  }

  async updateAsset(id: string, updates: Partial<Asset>): Promise<Asset> {
    const response = await apiService.put<AssetODataDto>(`${this.basePath}/${id}`, updates)
    return mapAsset(response.data)
  }

  async deleteAsset(id: string): Promise<void> {
    await apiService.delete(`${this.basePath}/${id}`)
  }

  async searchAssets(
    query: string,
    filters?: AssetSearchFilters,
    pagination?: PaginationRequest
  ): Promise<PaginatedResponse<Asset>> {
    return this.list(filters, pagination, query)
  }

  // Folder operations
  async getFolders(parentId?: string): Promise<AssetFolder[]> {
    const response = await apiService.get<AssetFolder[]>('/v1/folders', {
      params: { parentId },
    })
    return response.data
  }

  async createFolder(folder: Omit<AssetFolder, 'id' | 'createdAt' | 'updatedAt'>): Promise<AssetFolder> {
    const response = await apiService.post<AssetFolder>('/v1/folders', folder)
    return response.data
  }

  async updateFolder(id: string, updates: Partial<AssetFolder>): Promise<AssetFolder> {
    const response = await apiService.put<AssetFolder>(`/v1/folders/${id}`, updates)
    return response.data
  }

  async deleteFolder(id: string): Promise<void> {
    await apiService.delete(`/v1/folders/${id}`)
  }

  async moveAsset(assetId: string, targetFolderId: string | null): Promise<Asset> {
    const response = await apiService.post<AssetODataDto>(`${this.basePath}/${assetId}/move`, {
      folderId: targetFolderId,
    })
    return mapAsset(response.data)
  }

  async copyAsset(assetId: string, targetFolderId?: string): Promise<Asset> {
    const response = await apiService.post<AssetODataDto>(`${this.basePath}/${assetId}/copy`, {
      folderId: targetFolderId,
    })
    return mapAsset(response.data)
  }

  async bulkDelete(assetIds: string[]): Promise<void> {
    await apiService.post(`${this.basePath}/bulk-delete`, { ids: assetIds })
  }

  async bulkMove(assetIds: string[], targetFolderId: string | null): Promise<void> {
    await apiService.post(`${this.basePath}/bulk-move`, {
      ids: assetIds,
      folderId: targetFolderId,
    })
  }

  async getAssetsByTag(tag: string, pagination?: PaginationRequest): Promise<PaginatedResponse<Asset>> {
    const page = await this.list(undefined, pagination)
    return { ...page, items: page.items.filter(a => a.tags.includes(tag)) }
  }

  async getRecentAssets(limit: number = 10): Promise<Asset[]> {
    const page = await this.list(undefined, { pageSize: limit, sortBy: 'updatedAt', sortDirection: 'desc' })
    return page.items
  }

  async checkBrandCompliance(assetId: string): Promise<{
    compliant: boolean
    issues: string[]
  }> {
    const response = await apiService.post<{ compliant: boolean; issues: string[] }>(
      `${this.basePath}/${assetId}/check-compliance`
    )
    return response.data
  }
}

export const assetService = new AssetService()
